/**
 * Direction-aware inline editor for an activity's predecessors OR successors.
 * Buffer entries are direction-agnostic (one DB row per edge); this component
 * picks the right selectors and renders the "other side" of each edge.
 */
import React from "react";
import type {
  LogicActivity,
  LogicPredecessorEdge,
  LogicSuccessorEdge,
} from "../../../../models";
import { PredType, predTypeLabel } from "../../../../utils/p6";
import { colorForPrefix } from "../../../../utils/color";
import {
  EditOp,
  selectEditsAsPred,
  selectEditsAsSucc,
  selectIsRemoved,
  selectPendingUpdate,
  useEditBuffer,
} from "../EditBuffer/editBuffer";
import ActivityAutocomplete from "./ActivityAutocomplete/ActivityAutocomplete";
import { parseLagInput, formatLagInput } from "./lag";
import "./RelEditorGrid.css";

export type EditDirection = "predecessors" | "successors";

interface Props {
  focal: LogicActivity;
  direction: EditDirection;
  activityById: Map<number, LogicActivity>;
  /** Optional work_code -> takt color entry, mirrors the takt board's active
   * palette (trade vs work mode) so each pred/succ shows the same color as its
   * bar on the board. Falls back to the activity's stored Takt_Color. */
  colors?: Record<string, { fill: string; font: string }>;
}

const PRED_TYPES: PredType[] = ["PR_FS", "PR_SS", "PR_FF", "PR_SF"];

const STATUS_PILLS: Record<string, { label: string; cls: string }> = {
  TK_NotStart: { label: "Not Started", cls: "rel-status-pill--notstart" },
  TK_Active: { label: "In Progress", cls: "rel-status-pill--active" },
  TK_Complete: { label: "Completed", cls: "rel-status-pill--complete" },
};

/** Activity display name — never a bare task code on its own. */
function activityName(other: LogicActivity | undefined, fallback: string): string {
  return other?.task_name || other?.task_code || fallback;
}

/** Badge inputs for an activity row, sourced from the activity itself so a
 *  completed activity the takt board omits still badges correctly. An untagged
 *  activity (no work code) returns an empty code → a grey "untagged" badge,
 *  mirroring the dep editor — never a bare task code. `colors` carries the
 *  board's active color mode (trade vs work); it wins over the stored Takt_Color. */
function resolveBadge(
  other: LogicActivity | undefined,
  colors?: Record<string, { fill: string; font: string }>,
): { workCode: string; colorFill?: string } {
  const workCode = other?.work_code || "";
  if (!workCode) return { workCode: "" };
  const colorFill =
    colors?.[workCode]?.fill || other?.color || colorForPrefix(workCode);
  return { workCode, colorFill };
}

interface UnifiedEdge {
  edge_id: number;
  other_id: number;
  other_code: string;
  pred_type: PredType;
  lag_hours: number;
}

function unifyEdge(
  e: LogicPredecessorEdge | LogicSuccessorEdge,
  direction: EditDirection,
): UnifiedEdge {
  if (direction === "predecessors") {
    const p = e as LogicPredecessorEdge;
    return {
      edge_id: p.id,
      other_id: p.pred_activity_id,
      other_code: p.pred_task_code,
      pred_type: p.pred_type,
      lag_hours: p.lag_hours,
    };
  }
  const s = e as LogicSuccessorEdge;
  return {
    edge_id: s.id,
    other_id: s.succ_activity_id,
    other_code: s.succ_task_code,
    pred_type: s.pred_type,
    lag_hours: s.lag_hours,
  };
}

const RelEditorGrid: React.FC<Props> = ({
  focal, direction, activityById, colors,
}) => {
  const {
    state,
    addPredecessor,
    mutatePendingAdd,
    removeExistingEdge,
    updateExistingEdge,
    revertOp,
  } = useEditBuffer();

  const directionLabel = direction === "predecessors" ? "predecessor" : "successor";

  const focalSideEdits = React.useMemo(
    () =>
      direction === "predecessors"
        ? selectEditsAsSucc(state.edits, focal.id)
        : selectEditsAsPred(state.edits, focal.id),
    [state.edits, focal.id, direction],
  );

  const pendingAdds = focalSideEdits.filter(
    (e): e is Extract<EditOp, { kind: "add" }> => e.kind === "add",
  );

  const existingEdges = React.useMemo<UnifiedEdge[]>(() => {
    const raw =
      direction === "predecessors" ? focal.predecessors : focal.successors;
    return raw.map((e) => unifyEdge(e, direction));
  }, [focal, direction]);

  // Activities already linked to focal in this direction (existing minus
  // pending removes, plus pending adds) — excluded from autocomplete to
  // prevent duplicates. The focal itself is always excluded (no self-loop).
  const usedIds = React.useMemo(() => {
    const ids = new Set<number>();
    for (const e of existingEdges) {
      if (!selectIsRemoved(state.edits, e.edge_id)) ids.add(e.other_id);
    }
    for (const a of pendingAdds) {
      ids.add(direction === "predecessors" ? a.pred_id : a.succ_id);
    }
    ids.add(focal.id);
    return ids;
  }, [existingEdges, focal.id, state.edits, pendingAdds, direction]);

  const [autocompleteOpen, setAutocompleteOpen] = React.useState(false);

  const onPick = (chosen: LogicActivity) => {
    if (direction === "predecessors") {
      addPredecessor(focal.id, chosen.id, "PR_FS", 0);
    } else {
      addPredecessor(chosen.id, focal.id, "PR_FS", 0);
    }
    setAutocompleteOpen(false);
  };

  return (
    <div className="rel-editor-grid">
      {existingEdges.map((edge) => {
        const otherActivity = activityById.get(edge.other_id);
        const name = activityName(otherActivity, edge.other_code);
        const { workCode, colorFill } = resolveBadge(otherActivity, colors);
        return (
          <ExistingEdgeRow
            key={edge.edge_id}
            edge={edge}
            name={name}
            workCode={workCode}
            colorFill={colorFill}
            status={otherActivity?.status ?? null}
            startDate={otherActivity?.start ?? null}
            finishDate={otherActivity?.finish ?? null}
            totalFloat={otherActivity?.total_float ?? null}
            state={state}
            onRemove={() => removeExistingEdge(focal.id, edge.other_id, edge.edge_id)}
            onRevertRemove={() =>
              revertOp((e) => e.kind === "remove" && e.edge_id === edge.edge_id)
            }
            onUpdate={(predType, lagHours) => {
              // Edited back to the original values? Drop any pending update
              // rather than emit a no-op edit. The buffer doesn't track
              // originals — the caller does, since the original lives on the
              // edge object we're already iterating.
              if (predType === edge.pred_type && lagHours === edge.lag_hours) {
                revertOp((e) => e.kind === "update" && e.edge_id === edge.edge_id);
                return;
              }
              const succId =
                direction === "predecessors" ? focal.id : edge.other_id;
              const predId =
                direction === "predecessors" ? edge.other_id : focal.id;
              updateExistingEdge({
                edge_id: edge.edge_id,
                succ_id: succId,
                pred_id: predId,
                pred_type: predType,
                lag_hours: lagHours,
              });
            }}
            onRevertUpdate={() =>
              revertOp((e) => e.kind === "update" && e.edge_id === edge.edge_id)
            }
          />
        );
      })}

      {pendingAdds.map((add) => {
        const otherId = direction === "predecessors" ? add.pred_id : add.succ_id;
        const other = activityById.get(otherId);
        const { workCode: otherWorkCode, colorFill: otherColorFill } =
          resolveBadge(other, colors);
        return (
          <PendingAddRow
            key={add.tempId}
            add={add}
            otherName={activityName(other, `#${otherId}`)}
            otherWorkCode={otherWorkCode}
            otherColorFill={otherColorFill}
            otherStatus={other?.status ?? null}
            otherStart={other?.start ?? null}
            otherFinish={other?.finish ?? null}
            otherFloat={other?.total_float ?? null}
            onChangeType={(t) => mutatePendingAdd(add.tempId, { pred_type: t })}
            onChangeLag={(h) => mutatePendingAdd(add.tempId, { lag_hours: h })}
            onRemove={() =>
              revertOp((e) => e.kind === "add" && e.tempId === add.tempId)
            }
          />
        );
      })}

      {autocompleteOpen ? (
        <div className="rel-editor-grid-row">
          <div className="rel-editor-cell rel-editor-cell-activity">
            <ActivityAutocomplete
              activityById={activityById}
              excludeIds={usedIds}
              onPick={onPick}
              onCancel={() => setAutocompleteOpen(false)}
              placeholder={`Search ${directionLabel}…`}
            />
          </div>
        </div>
      ) : (
        <button
          type="button"
          className="rel-editor-add-btn"
          onClick={() => setAutocompleteOpen(true)}
        >
          + Add {directionLabel}
        </button>
      )}
    </div>
  );
};

// ---------------------------------------------------------------------------
// Activity cell — badge + name + status pill on top, dates + float below.
// ALWAYS shows the activity name + status; an untagged activity gets a grey
// badge (no code, like the dep editor) — never a bare task code.
// ---------------------------------------------------------------------------

const ActivityCell: React.FC<{
  name: string;
  /** Takt_Work code; empty → grey "untagged" badge. */
  workCode?: string;
  colorFill?: string;
  status: string | null;
  startDate: string | null;
  finishDate: string | null;
  totalFloat: number | null;
}> = ({ name, workCode, colorFill, status, startDate, finishDate, totalFloat }) => {
  const start = formatShortDate(startDate);
  const finish = formatShortDate(finishDate);
  const dates = start && finish ? `${start} → ${finish}` : start ?? finish ?? "";
  const pill = status ? STATUS_PILLS[status] : undefined;
  return (
    <div
      className="rel-editor-cell rel-editor-cell-activity rel-editor-cell-activity--detailed"
      title={`${workCode || "untagged"} — ${name}`}
    >
      <div className="rel-editor-activity-line1">
        <span
          className={`rel-editor-activity-badge${workCode ? "" : " rel-editor-activity-badge--untagged"}`}
          style={workCode && colorFill ? { backgroundColor: colorFill } : undefined}
          aria-label={workCode ? undefined : "untagged"}
        >
          {workCode}
        </span>
        <span className="rel-editor-activity-name">{name}</span>
        {pill && <span className={`rel-status-pill ${pill.cls}`}>{pill.label}</span>}
      </div>
      {(dates || totalFloat !== null) && (
        <div className="rel-editor-activity-line2">
          {dates && <span>{dates}</span>}
          {totalFloat !== null && (
            <span className="rel-editor-activity-float">F: {totalFloat}d</span>
          )}
        </div>
      )}
    </div>
  );
};

function formatShortDate(iso: string | null): string {
  if (!iso) return "";
  const m = iso.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (!m) return iso.slice(0, 10);
  return `${parseInt(m[2], 10)}/${parseInt(m[3], 10)}`;
}

// ---------------------------------------------------------------------------
// Existing edge row — original or with pending update / pending remove
// ---------------------------------------------------------------------------

const ExistingEdgeRow: React.FC<{
  edge: UnifiedEdge;
  name: string;
  workCode?: string;
  colorFill?: string;
  status: string | null;
  startDate: string | null;
  finishDate: string | null;
  totalFloat: number | null;
  state: { edits: EditOp[] };
  onRemove: () => void;
  onRevertRemove: () => void;
  onUpdate: (predType: PredType, lagHours: number) => void;
  onRevertUpdate: () => void;
}> = ({ edge, name, workCode, colorFill, status, startDate, finishDate, totalFloat, state, onRemove, onRevertRemove, onUpdate, onRevertUpdate }) => {
  const removed = selectIsRemoved(state.edits, edge.edge_id);
  const update = selectPendingUpdate(state.edits, edge.edge_id);

  const predType = update?.pred_type ?? edge.pred_type;
  const lagHours = update?.lag_hours ?? edge.lag_hours;

  const className = [
    "rel-editor-grid-row",
    removed ? "removed" : "",
    update ? "updated" : "",
  ]
    .filter(Boolean)
    .join(" ");

  return (
    <div className={className}>
      <ActivityCell
        name={name}
        workCode={workCode}
        colorFill={colorFill}
        status={status}
        startDate={startDate}
        finishDate={finishDate}
        totalFloat={totalFloat}
      />
      <div className="rel-editor-cell rel-editor-cell-type">
        <select
          value={predType}
          disabled={removed}
          onChange={(e) => onUpdate(e.target.value as PredType, lagHours)}
        >
          {PRED_TYPES.map((t) => (
            <option key={t} value={t}>
              {predTypeLabel(t)}
            </option>
          ))}
        </select>
      </div>
      <div className="rel-editor-cell rel-editor-cell-lag">
        <LagInput value={lagHours} disabled={removed} onCommit={(h) => onUpdate(predType, h)} />
      </div>
      <div className="rel-editor-cell rel-editor-cell-actions">
        {removed ? (
          <button type="button" className="rel-editor-mini-btn" title="Revert remove" onClick={onRevertRemove}>
            ↻
          </button>
        ) : (
          <>
            {update && (
              <button
                type="button"
                className="rel-editor-mini-btn"
                title="Revert pending change"
                onClick={onRevertUpdate}
              >
                ↻
              </button>
            )}
            <button
              type="button"
              className="rel-editor-mini-btn"
              title="Remove relationship"
              onClick={onRemove}
            >
              ×
            </button>
          </>
        )}
      </div>
    </div>
  );
};

// ---------------------------------------------------------------------------
// Pending-add row — not yet on the backend
// ---------------------------------------------------------------------------

const PendingAddRow: React.FC<{
  add: Extract<EditOp, { kind: "add" }>;
  otherName: string;
  otherWorkCode?: string;
  otherColorFill?: string;
  otherStatus: string | null;
  otherStart: string | null;
  otherFinish: string | null;
  otherFloat: number | null;
  onChangeType: (t: PredType) => void;
  onChangeLag: (h: number) => void;
  onRemove: () => void;
}> = ({ add, otherName, otherWorkCode, otherColorFill, otherStatus, otherStart, otherFinish, otherFloat, onChangeType, onChangeLag, onRemove }) => (
  <div className="rel-editor-grid-row added">
    <ActivityCell
      name={otherName}
      workCode={otherWorkCode}
      colorFill={otherColorFill}
      status={otherStatus}
      startDate={otherStart}
      finishDate={otherFinish}
      totalFloat={otherFloat}
    />
    <div className="rel-editor-cell rel-editor-cell-type">
      <select value={add.pred_type} onChange={(e) => onChangeType(e.target.value as PredType)}>
        {PRED_TYPES.map((t) => (
          <option key={t} value={t}>
            {predTypeLabel(t)}
          </option>
        ))}
      </select>
    </div>
    <div className="rel-editor-cell rel-editor-cell-lag">
      <LagInput value={add.lag_hours} onCommit={onChangeLag} />
    </div>
    <div className="rel-editor-cell rel-editor-cell-actions">
      <button type="button" className="rel-editor-mini-btn" title="Discard" onClick={onRemove}>
        ×
      </button>
    </div>
  </div>
);

// ---------------------------------------------------------------------------
// Lag input — text field that parses "2d" / "16h" / "0" / "-1d"
// ---------------------------------------------------------------------------

const LagInput: React.FC<{
  value: number;
  disabled?: boolean;
  onCommit: (lagHours: number) => void;
}> = ({ value, disabled, onCommit }) => {
  const [text, setText] = React.useState(formatLagInput(value));
  const [error, setError] = React.useState(false);

  React.useEffect(() => {
    setText(formatLagInput(value));
    setError(false);
  }, [value]);

  const tryCommit = () => {
    const parsed = parseLagInput(text);
    if (parsed === null) {
      setError(true);
      return;
    }
    setError(false);
    if (parsed !== value) onCommit(parsed);
    else setText(formatLagInput(parsed));
  };

  return (
    <input
      className={`rel-editor-lag-input ${error ? "error" : ""}`}
      type="text"
      value={text}
      disabled={disabled}
      onChange={(e) => setText(e.target.value)}
      onBlur={tryCommit}
      onKeyDown={(e) => {
        if (e.key === "Enter") {
          e.preventDefault();
          tryCommit();
          (e.target as HTMLInputElement).blur();
        } else if (e.key === "Escape") {
          e.preventDefault();
          setText(formatLagInput(value));
          setError(false);
          (e.target as HTMLInputElement).blur();
        }
      }}
    />
  );
};

export default RelEditorGrid;
